import React, { useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { geolocationChanged } from '../../../store';
import { useGlMap } from '../../../contexts/context';
import { MIN_MAP_EDITABLE_ZOOM_LEVEL } from '../../../../common/utils/constants/constants';
import { Icon } from '../../../../common/components/icon/Icon';

// https://docs.mapbox.com/mapbox-gl-js/api/#excluding-gl-js-explicitly-from-transpilation
// eslint-disable-next-line
import mapboxgl from '!mapbox-gl';

import './GeolocationControl.scss';

const GEOLOCATION_ZOOM_THRESHOLD = 0.5;

const GeolocateControl = ({
  position,
  showAccuracyCircle,
}: {
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  showAccuracyCircle?: boolean;
}) => {
  const glMap = useGlMap();
  const dispatch = useDispatch();
  const geolocateRef = useRef<mapboxgl.GeolocateControl | null>(null);

  const handleGeolocateClick = useCallback(() => {
    if (geolocateRef.current) {
      geolocateRef.current.trigger();
    }
  }, []);

  useEffect(() => {
    if (!glMap) {
      return;
    }

    const dispatchGeolocationChanged = ({ coords }: any) =>
      dispatch(
        geolocationChanged({
          latitude: coords.latitude,
          longitude: coords.longitude,
        }),
      );

    const geolocate = new mapboxgl.GeolocateControl({
      showAccuracyCircle,
      fitBoundsOptions: {
        maxZoom: MIN_MAP_EDITABLE_ZOOM_LEVEL + GEOLOCATION_ZOOM_THRESHOLD,
      },
    });
    geolocateRef.current = geolocate;

    geolocate.on('geolocate', dispatchGeolocationChanged);

    glMap.addControl(geolocate, position);

    return () => {
      geolocate.off('geolocate', dispatchGeolocationChanged);
      // sometimes it errors on HMR while development
      try {
        glMap.removeControl(geolocate);
        // eslint-disable-next-line no-empty
      } catch (err) {}

      geolocateRef.current = null;
    };
  }, [glMap, position, showAccuracyCircle, dispatch]);

  return (
    <button
      className="geolocation-control"
      type="button"
      onClick={handleGeolocateClick}
    >
      <Icon className="geolocation-control__icon" name="location" />
    </button>
  );
};

export default GeolocateControl;
